import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useDowngradePolicy } from '@/hooks/useDowngradePolicy';
import { getDowngradeTermsClause } from '@/lib/downgradePolicyText';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Globe } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface TermsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TermsModal({ open, onOpenChange }: TermsModalProps) {
  const { nonEnterpriseDowngradeAppliesFirstOfMonth } = useDowngradePolicy();
  const { language, setLanguage } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4 pr-6">
            <div>
              <DialogTitle className="text-2xl">
                {language === 'en' ? 'Terms of Service' : '이용약관'}
              </DialogTitle>
              <DialogDescription>
                {language === 'en'
                  ? 'Please review the ALT Generation Terms of Service. (Effective date: April 1, 2026)'
                  : 'ALT Generation 서비스 이용약관을 확인해주세요. (시행일: 2026년 4월 1일)'}
              </DialogDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLanguage(language === 'ko' ? 'en' : 'ko')}
              className="shrink-0"
            >
              <Globe className="w-4 h-4 mr-1" />
              {language === 'ko' ? 'English' : '한국어'}
            </Button>
          </div>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[60vh] pr-4">
          {language === 'en' ? (
            <TermsContentEn downgradeAppliesFirstOfMonth={nonEnterpriseDowngradeAppliesFirstOfMonth} />
          ) : (
            <TermsContentKo downgradeAppliesFirstOfMonth={nonEnterpriseDowngradeAppliesFirstOfMonth} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TermsContentKo({ downgradeAppliesFirstOfMonth }: { downgradeAppliesFirstOfMonth: boolean }) {
  return (
    <div className="space-y-6 text-sm">

      {/* 사업자 정보 */}
      <section className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-bold text-base mb-2">사업자 정보 (전자상거래법 제10조)</h3>
        <div className="text-gray-700 space-y-1 leading-relaxed">
          <p>• <strong>상호:</strong> 지호아이앤씨 (이하 "회사")</p>
          <p>• <strong>서비스명:</strong> ALT Generation</p>
          <p>• <strong>대표자:</strong> 최정윤</p>
          <p>• <strong>소재지:</strong> 서울 중구 다산로 106 (신당동, 한두빌딩) 5층</p>
          <p>• <strong>이메일:</strong> alt.generation@outlook.com</p>
          <p>• <strong>전화:</strong> 02-2636-4664</p>
          <p>• <strong>서비스 URL:</strong> https://alt.aigeneration-tech.com</p>
        </div>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제1조 (목적)</h3>
        <p className="text-gray-700 leading-relaxed">
          본 약관은 지호아이앤씨(이하 "회사")이 운영하는 ALT Generation 웹사이트(https://alt.aigeneration-tech.com, 이하 "서비스")를 이용함에 있어 회사와 이용자 간의 권리, 의무, 접임사항 및 서비스 이용에 관한 조건을 규정함을 목적으로 합니다.
        </p>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제2조 (정의)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>"서비스"란 회사가 제공하는 AI 기반 이미지 ALT 텍스트 자동 생성 SaaS(Software as a Service) 관련 제반 서비스를 의미합니다.</li>
          <li>"이용자"란 본 약관에 따라 서비스를 이용하는 회원 및 비회원을 말합니다.</li>
          <li>"회원"이란 회사와 이용계약을 체결하고 계정을 부여받은 자를 말합니다.</li>
          <li>"무료 회원"이란 유료 요금제에 가입하지 않은 회원으로, 무료 플랜 한도 내에서 서비스를 이용하는 자를 말합니다.</li>
          <li>"유료 회원"이란 회사가 정한 유료 요금제에 가입하여 이용료를 납부하고 서비스를 이용하는 회원을 말합니다.</li>
          <li>"ALT 텍스트"란 이미지에 대한 접근성 및 SEO 목적의 대체 텍스트 설명을 의미합니다.</li>
          <li>"API 키"란 회사가 제공하는 Open API를 이용하기 위해 발급되는 인증 코드를 의미하며, 스타트(Starter) 플랜 이상의 회원에게만 발급됩니다.</li>
          <li>"요금제"란 회사가 제공하는 서비스 이용 등급으로, 무료(Free), 스타트(Starter), 프로(Professional), 엔터프라이즈(Enterprise) 플랜으로 구성됩니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제3조 (약관의 효력 및 변경)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>본 약관은 서비스 화면에 게시하거나 이용자에게 공지하는 시점부터 효력이 발생합니다.</li>
          <li>회사는 관련 법령을 위반하지 않는 범위에서 본 약관을 변경할 수 있으며, 변경 시 적용일자 및 변경사유를 명시하여 7일 이전(이용자에게 불리한 변경의 경우 30일 이전)에 서비스 공지사항, 이메일 등을 통해 공지합니다.</li>
          <li>이용자가 변경된 약관에 동의하지 않는 경우 서비스 이용을 중단하고 이용계약을 해지할 수 있습니다. 변경 이후에도 서비스를 계속 이용하면 변경에 동의한 것으로 봅니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제4조 (서비스의 제공)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>회사는 다음과 같은 서비스를 제공합니다.
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>웹사이트 URL 크롤링을 통한 이미지 수집 및 ALT 텍스트 자동 생성</li>
              <li>이미지 URL 직접 입력을 통한 ALT 텍스트 생성</li>
              <li>이미지 이미지 업로드를 통한 ALT 텍스트 생성</li>
              <li>생성된 ALT 텍스트 조회·수정·관리 및 이력 제공</li>
              <li>Open API를 통한 외부 시스템 연동 서비스</li>
              <li>기타 회사가 추가하거나 제휴를 통해 제공하는 서비스</li>
            </ul>
          </li>
          <li>서비스는 연중무휴 24시간 제공을 원칙으로 하나, 시스템 점검·장애·개선 작업 등의 경우 일시적으로 중단될 수 있습니다. 예정된 점검은 사전에 공지합니다.</li>
          <li>회사는 서비스 운영상 필요한 경우, 사전 고지 후 서비스의 내용을 변경하거나 종료할 수 있습니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제5조 (회원가입 및 계약의 성립)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>이용자는 회사가 정한 가입 양식에 따라 정보를 기입하고 본 약관 및 개인정보처리방침에 동의함으로써 회원가입을 신청합니다.</li>
          <li>다음 각 호에 해당하는 신청은 승낙을 거부하거나 사후에 이용계약을 해지할 수 있습니다.
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>이전에 본 약관에 의해 회원자격을 상실한 적이 있는 경우</li>
              <li>타인의 명의를 도용하거나 허위 정보를 등록한 경우</li>
              <li>만 14세 미만인 경우</li>
              <li>기타 회사의 기술·운영상 이유로 승낙이 어려운 경우</li>
            </ul>
          </li>
          <li>회원가입 계약은 회사의 승낙의사가 이용자에게 도달한 시점에 성립합니다.</li>
          <li>탈퇴한 회원은 탈퇴일로부터 30일이 경과한 후 동일 이메일로 재가입이 가능합니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제6조 (계정 관리)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>회원은 아이디와 비밀번호를 직접 관리해야 하며, 제3자에게 양도하거나 이용하게 할 수 없습니다.</li>
          <li>계정 정보 유출, 도용, 무단 사용 등을 인지한 경우 즉시 회사에 신고하고 안내에 따라야 합니다.</li>
          <li>회원의 관리 소홀로 인한 피해에 대해 회사는 책임을 지지 않습니다.</li>
          <li>회원은 프로필관리에서 언제든지 개인정보를 열람하고 수정할 수 있습니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제7조 (유료 서비스 및 요금제)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>서비스는 다음 요금제로 제공되며, 요금 및 제공 내용은 회사 사정에 따라 변경될 수 있습니다.
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>무료(Free): 월 최대 40건, 일 최대 20건 ALT 텍스트 생성 가능. API 키 발급 불가.</li>
              <li>스타트(Starter): 월 최대 1,000건. API 키 발급 가능. 월 ₩9,900.</li>
              <li>프로(Professional): 월 최대 5,000건. API 키 발급 가능. 월 ₩29,000.</li>
              <li>엔터프라이즈(Enterprise): 월 최대 5,000건 초과. API 키 발급 가능. 별도 협의를 통해 계약을 체결하며, 해당 계약에서 달리 정한 사항은 본 약관에 우선합니다.</li>
            </ul>
          </li>
          <li>유료 서비스는 선납제를 원칙으로 하며, 제8조에서 정한 결제 방식(계좌이체 또는 카드 자동결제)으로 결제합니다.</li>
          <li><strong>API 키:</strong> API 키를 통한 외부 시스템 연동은 스타트(Starter) 플랜 이상에서만 이용 가능합니다.</li>
          <li><strong>요금제 업그레이드:</strong> 계좌이체는 회사 지정 계좌 입금 후 관리자 확인 시, 카드 결제는 즉시 플랜이 적용됩니다.</li>
          <li><strong>요금제 다운그레이드:</strong> {getDowngradeTermsClause(downgradeAppliesFirstOfMonth)}</li>
          <li>회사는 요금제 변경 시 최소 30일 전 이메일 또는 서비스 공지를 통해 안내합니다.</li>
          <li>API 사용량이 요금제 한도를 초과하는 경우 서비스 이용이 제한될 수 있습니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제8조 (결제 방식 및 자동 정기결제)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>결제 방식은 계좌이체(무통장입금)와 카드 자동결제 중 이용자가 선택할 수 있습니다.</li>
          <li><strong>계좌이체:</strong> 결제 계좌 정보는 요금제 업그레이드 신청 시 이메일 또는 서비스 화면을 통해 안내되며, 입금 확인 후 관리자가 플랜을 활성화합니다.</li>
          <li><strong>카드 자동결제(정기결제):</strong>
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>카드를 등록해 결제하면, 이후 매 결제 주기(월 1회, 매월 1일)마다 등록된 카드로 요금이 자동으로 청구됩니다.</li>
              <li>회사는 자동결제 예정일 이전에 결제 예정 사실을 이메일로 사전 안내합니다.</li>
              <li>자동결제를 중단하려면 요금제를 무료(Free) 플랜으로 변경하거나 등록된 카드를 삭제하면 되며, 처리 시점 이후 도래하는 결제 주기부터 자동결제가 발생하지 않습니다.</li>
              <li>카드 결제 정보는 결제대행사를 통해 처리되며 회사가 카드번호 원본을 직접 저장하지 않습니다. 자세한 사항은 개인정보처리방침 제5조(개인정보 처리의 위탁)를 참고하시기 바랍니다.</li>
            </ul>
          </li>
          <li>입금 또는 카드결제 후 영업일 기준 1일 이내 플랜이 적용되지 않는 경우 이메일(alt.generation@outlook.com)로 문의하시기 바랍니다.</li>
          <li>회사는 이용자의 요청에 따라 세금계산서 또는 현금영수증을 발행하며, 발행을 위해 필요한 사업자 정보(상호, 사업자등록번호 등)를 결제 전 별도로 입력받을 수 있습니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제9조 (팀 서비스)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>회원은 팀을 생성하여 다른 회원(팀원)을 초대하고 요금제 및 사용량을 함께 이용할 수 있습니다.</li>
          <li>팀을 생성한 회원(이하 "팀장")은 팀 요금제의 결제 주체가 되며, 팀원 초대·제외, 팀 삭제, 팀장 권한 이전에 대한 권한과 책임을 가집니다.</li>
          <li>팀원의 서비스 이용 이력(ALT 텍스트 생성 이력, 사용량 등)은 팀 운영 관리 목적으로 팀장이 조회할 수 있습니다.</li>
          <li>팀원이 팀을 탈퇴하거나 팀장에 의해 제외되는 경우, 해당 회원은 팀 요금제 혜택을 더 이상 이용할 수 없으며 개인 요금제로 전환됩니다. 팀 소속 중 생성된 ALT 텍스트 이력은 팀에 귀속되어 유지됩니다.</li>
          <li>팀장은 팀을 탈퇴하기 전 팀장 권한을 다른 팀원에게 이전해야 합니다. 팀장 권한 이전 시 팀의 요금제는 그대로 유지됩니다.</li>
          <li>팀이 삭제되는 경우 팀의 요금제 이용은 즉시 종료되며, 팀원들은 서비스를 더 이상 이용할 수 없습니다. 팀 삭제로 인한 잔여 기간 환불은 제공되지 않습니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제10조 (서비스 이용의 제한 및 중지)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>회사는 전시, 사변, 천재지변 또는 이에 준하는 국가비상사태가 발생하거나 발생할 우려가 있는 경우와 전기통신사업법에 의한 기간통신사업자가 전기통신서비스를 중지하는 등 부득이한 사유가 있는 경우에는 서비스의 전부 또는 일부를 제한하거나 중지할 수 있습니다.</li>
          <li>회사는 이용자가 본 약관의 의무를 위반하거나 서비스의 정상적인 운영을 방해한 경우, 경고, 일시정지, 영구이용정지 등으로 서비스 이용을 단계적으로 제한할 수 있습니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제11조 (청약철회 및 환불 정책)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>회원은 유료 서비스 결제 후 서비스를 사용하지 않은 상태(ALT 텍스트 생성, API 호출 등 미사용)라면 결제일로부터 7일 이내에 청약철회(결제 취소)를 요청할 수 있습니다. 요건을 충족하면 결제 금액 전액을 환불합니다.</li>
          <li>다음 각 호에 해당하는 경우 청약철회 및 환불이 제한됩니다.
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>결제 후 ALT 텍스트 생성, API 호출 등 서비스를 1회라도 이용한 경우</li>
              <li>결제일로부터 7일이 경과한 경우</li>
              <li>이용자의 단순 변심 또는 요금제 내용 미확인으로 인한 경우</li>
              <li>프로모션·이벤트 등을 통해 무상으로 취득한 유료 서비스</li>
            </ul>
          </li>
          <li>환불이 제한되는 경우에도 요금제를 낮추면 이번 결제 기간이 끝날 때까지는 계속 이용할 수 있고, 이후 자동으로 낮아진 요금제로 전환됩니다. 이미 결제된 기간의 잔여 서비스에 대한 별도 환불은 제공되지 않습니다.</li>
          <li>회사의 귀책사유로 인한 서비스 장애(24시간 이상 지속 장애)의 경우 장애 기간에 비례한 이용기간 연장 또는 환불이 제공될 수 있습니다.</li>
          <li>환불 문의는 이메일(alt.generation@outlook.com)로 신청하며, 회사는 수령일로부터 3영업일 이내에 처리합니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제12조 (서비스 이용 시간 및 장애)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>서비스는 연중무휴 24시간을 원칙으로 하나, 다음의 경우 사전·사후 고지 후 중단될 수 있습니다.
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>시스템 정기점검, 업그레이드, 긴급 보안 패치</li>
              <li>서버·네트워크 장애, 천재지변, 국가 비상사태</li>
              <li>외부 AI 서비스 제공사(OpenAI 등) 장애</li>
            </ul>
          </li>
          <li>회사는 서비스 중단으로 인한 손해에 대해, 회사의 고의 또는 중과실이 없는 한 책임을 지지 않습니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제13조 (이용자 금지행위)</h3>
        <p className="text-gray-700 leading-relaxed mb-2">이용자는 다음 행위를 하여서는 안 됩니다.</p>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>회원가입 또는 서비스 이용 시 허위 정보 등록</li>
          <li>타인의 계정 정보 도용 또는 무단 사용</li>
          <li>서비스를 이용하여 타인의 저작권, 초상권, 개인정보를 침해하는 행위</li>
          <li>유해 콘텐츠(음란물, 폭력, 혐오 등)가 포함된 이미지를 업로드하거나 크롤링하는 행위</li>
          <li>크롤링 시 대상 사이트의 robots.txt 규정을 무시하거나, 과도한 트래픽으로 해당 사이트에 피해를 주는 행위</li>
          <li>API 키를 무단으로 공유하거나 제3자에게 양도·판매하는 행위</li>
          <li>서비스의 소스코드 역공학, 취약점 탐색, 무단 크롤링 등 시스템을 해치는 행위</li>
          <li>자동화 프로그램을 이용하여 요금제 한도를 초과하거나 서비스를 남용하는 행위</li>
          <li>기타 법령, 본 약관, 공서양속에 반하는 행위</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제14조 (계약해지 및 회원탈퇴)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>회원은 회원탈퇴 메뉴 또는 이메일을 통해 언제든지 탈퇴를 신청할 수 있습니다.</li>
          <li>유료 구독 중인 회원이 탈퇴하는 경우 구독은 즉시 해지되며, 잔여 기간에 대한 환불은 제공되지 않습니다. 등록된 카드가 있는 경우 자동결제도 함께 중단됩니다.</li>
          <li>탈퇴 후 1년간 회원의 데이터(ALT 텍스트 이력, API 키 등)가 보존되며, 이 기간 내에 탈퇴를 취소하고 계정을 복구할 수 있습니다. 1년 경과 후에는 모든 데이터가 자동 삭제되며 복구되지 않습니다. 단, 관계 법령에 의해 보존이 요구되는 정보는 해당 기간 동안 별도 보관됩니다.</li>
          <li>탈퇴 후 1년 이내에는 이메일을 통해 탈퇴를 취소하고 계정을 복구할 수 있습니다. 다만 탈퇴 처리 과정에서 파기된 카드 결제 정보, 해지된 팀 소속, 초기화된 요금제 등은 계정 복구 후에도 다시 복원되지 않으며, 필요 시 다시 설정해야 합니다.</li>
          <li>탈퇴한 회원은 탈퇴일로부터 30일이 경과한 후 동일 이메일로 재가입이 가능합니다.</li>
          <li>회사는 다음 각 호에 해당하는 경우 이용계약을 해지하거나 서비스 이용을 제한할 수 있습니다.
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>본 약관의 금지행위를 한 경우</li>
              <li>이용요금을 30일 이상 연체한 경우</li>
              <li>서비스를 불법적 목적으로 이용한 경우</li>
            </ul>
          </li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제15조 (면책조항)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>천재지변, 전쟁, 테러, 해킹 등 불가항력으로 인한 서비스 중단에 대해 회사는 책임을 지지 않습니다.</li>
          <li><strong>AI 생성 결과물:</strong> 본 서비스는 AI 모델을 활용하므로 생성된 ALT 텍스트의 정확성, 완전성, 적합성을 보증하지 않습니다. 최종 검토 및 수정은 이용자의 책임입니다.</li>
          <li>이용자가 크롤링하는 외부 웹사이트의 저작권 침해, 이용약관 위반 등에 대한 법적 책임은 이용자에게 있습니다.</li>
          <li>이용자가 서비스에 업로드한 이미지의 저작권, 초상권, 개인정보 등에 관한 책임은 이용자에게 있습니다.</li>
          <li>외부 AI 서비스(OpenAI 등)의 장애·정책 변경으로 인한 서비스 중단 또는 품질 저하에 대해 회사는 책임을 지지 않습니다.</li>
          <li>무료 서비스 이용과 관련하여 발생하는 손해에 대해, 회사의 고의 또는 중과실이 없는 한 책임을 지지 않습니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제16조 (분쟁 해결)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>서비스 이용과 관련한 분쟁은 먼저 이메일(alt.generation@outlook.com)을 통해 쌍방 합의로 해결합니다.</li>
          <li>합의가 이루어지지 않을 경우 소비자분쟁조정위원회 등 관련 기관의 조정에 따를 수 있습니다.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">제17조 (준거법 및 재판관할)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>본 약관과 관련된 사항에 대하여는 대한민국 법을 준거법으로 합니다.</li>
          <li>서비스 이용으로 발생한 분쟁에 대해 소송이 제기되는 경우 민사소송법 등 관계 법령에 따른 관할법원에 제소합니다.</li>
        </ol>
      </section>

      <div className="pt-4 border-t mt-6 space-y-1">
        <p className="text-gray-600 text-xs">공고일자: 2026년 3월 27일</p>
        <p className="text-gray-600 text-xs">시행일자: 2026년 4월 1일</p>
        <p className="text-gray-600 text-xs">개정일자: 2026년 8월 10일 (재판관할 조항을 관계 법령에 따른 관할로 변경, 무료 플랜 월 한도를 40건으로 조정)</p>
        <p className="text-gray-600 text-xs">개정일자: 2026년 8월 11일 (카드 자동결제 청구 주기를 "최초 결제일 기준 매월 동일한 날"에서 실제 운영 방식인 "매월 1일"로 정정)</p>
        <p className="text-xs pt-1">
          <Link to="/terms/archive" target="_blank" rel="noopener noreferrer" className="text-primary underline">
            이전 버전 보기
          </Link>
        </p>
      </div>
    </div>
  );
}

function TermsContentEn({ downgradeAppliesFirstOfMonth }: { downgradeAppliesFirstOfMonth: boolean }) {
  return (
    <div className="space-y-6 text-sm">
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
        This English version is a reference translation provided for convenience. In the event of any discrepancy between the Korean original and this English translation, the Korean original shall prevail.
      </p>

      {/* Business information */}
      <section className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-bold text-base mb-2">Business Information (Article 10 of the Act on Consumer Protection in Electronic Commerce)</h3>
        <div className="text-gray-700 space-y-1 leading-relaxed">
          <p>• <strong>Trade name:</strong> JIHO I&C Co., Ltd. (hereinafter the "Company")</p>
          <p>• <strong>Service name:</strong> ALT Generation</p>
          <p>• <strong>Representative:</strong> Choi Jeong-yun</p>
          <p>• <strong>Address:</strong> 5F, 106 Dasan-ro, Jung-gu, Seoul, Republic of Korea</p>
          <p>• <strong>Email:</strong> alt.generation@outlook.com</p>
          <p>• <strong>Phone:</strong> 02-2636-4664</p>
          <p>• <strong>Service URL:</strong> https://alt.aigeneration-tech.com</p>
        </div>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 1 (Purpose)</h3>
        <p className="text-gray-700 leading-relaxed">
          These Terms of Service set forth the rights, obligations, and responsibilities of, and other conditions relating to, the use of the ALT Generation website (https://alt.aigeneration-tech.com, the "Service") operated by JIHO I&C Co., Ltd. (the "Company"), as between the Company and users.
        </p>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 2 (Definitions)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>"Service" means all services related to the AI-based, image ALT text auto-generation SaaS (Software as a Service) provided by the Company.</li>
          <li>"User" means a member or non-member who uses the Service under these Terms.</li>
          <li>"Member" means a person who has entered into a service agreement with the Company and been granted an account.</li>
          <li>"Free member" means a member who has not subscribed to a paid plan and uses the Service within the limits of the free plan.</li>
          <li>"Paid member" means a member who has subscribed to a paid plan set by the Company, pays the applicable fee, and uses the Service.</li>
          <li>"ALT text" means alternative text descriptions of images for accessibility and SEO purposes.</li>
          <li>"API key" means the authentication code issued to use the Company's Open API, which is issued only to members on the Starter plan or above.</li>
          <li>"Plan" means a tier of Service usage offered by the Company, consisting of the Free, Starter, Professional, and Enterprise plans.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 3 (Effectiveness and Amendment of These Terms)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>These Terms become effective from the time they are posted on the Service or notified to users.</li>
          <li>The Company may amend these Terms to the extent it does not violate applicable law. Amendments will be announced through the Service's notice board, email, or other means, specifying the effective date and reason for the change, at least 7 days before the change takes effect (at least 30 days before, for changes disadvantageous to users).</li>
          <li>If a user does not agree to the amended Terms, the user may stop using the Service and terminate the service agreement. Continued use of the Service after the amendment takes effect is deemed acceptance of the amendment.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 4 (Provision of the Service)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>The Company provides the following services.
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>Image collection and automatic ALT text generation via website URL crawling</li>
              <li>ALT text generation via direct image URL input</li>
              <li>ALT text generation via image upload</li>
              <li>Viewing, editing, and management of generated ALT text, and provision of history</li>
              <li>Integration with external systems via the Open API</li>
              <li>Other services added by the Company or provided through partnerships</li>
            </ul>
          </li>
          <li>The Service is, in principle, provided 24 hours a day, year-round, but may be temporarily suspended for system maintenance, failures, or improvement work. Scheduled maintenance will be announced in advance.</li>
          <li>Where necessary for the operation of the Service, the Company may change or discontinue the content of the Service after prior notice.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 5 (Membership Registration and Formation of the Agreement)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>A user applies for membership by entering information in the sign-up form prescribed by the Company and agreeing to these Terms and the Privacy Policy.</li>
          <li>The Company may refuse to accept, or may subsequently terminate the service agreement for, an application in any of the following cases.
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>The applicant has previously lost membership status under these Terms</li>
              <li>The applicant has used another person's identity or registered false information</li>
              <li>The applicant is under 14 years of age</li>
              <li>Other cases where acceptance is difficult for technical or operational reasons of the Company</li>
            </ul>
          </li>
          <li>The membership agreement is formed when the Company's acceptance reaches the user.</li>
          <li>A withdrawn member may re-register with the same email address after 30 days have elapsed from the date of withdrawal.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 6 (Account Management)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>Members must manage their own ID and password and may not transfer or allow a third party to use them.</li>
          <li>If a member becomes aware of leakage, theft, or unauthorized use of account information, the member must immediately report it to the Company and follow the Company's instructions.</li>
          <li>The Company is not liable for damages arising from a member's failure to manage their account properly.</li>
          <li>Members may view and modify their personal information at any time in profile management.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 7 (Paid Services and Plans)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>The Service is offered under the following plans; fees and included features may change at the Company's discretion.
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>Free: up to 40 ALT text generations per month, up to 20 per day. API key not available.</li>
              <li>Starter: up to 1,000 per month. API key available. ₩9,900/month.</li>
              <li>Professional: up to 5,000 per month. API key available. ₩29,000/month.</li>
              <li>Enterprise: more than 5,000 per month. API key available. Contracted separately by negotiation; where such a contract provides otherwise, it takes precedence over these Terms.</li>
            </ul>
          </li>
          <li>Paid services are, in principle, prepaid and are billed using the payment method set out in Article 8 (bank transfer or automatic card billing).</li>
          <li><strong>API key:</strong> Integration with external systems via an API key is available only on the Starter plan or above.</li>
          <li><strong>Plan upgrades:</strong> For bank transfer, the plan is activated once the administrator confirms receipt of payment into the Company's designated account; for card payment, the plan is applied immediately.</li>
          <li><strong>Plan downgrades:</strong> {getDowngradeTermsClause(downgradeAppliesFirstOfMonth)}</li>
          <li>The Company will notify users of plan changes at least 30 days in advance by email or service notice.</li>
          <li>Service use may be restricted if API usage exceeds the plan's limit.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 8 (Payment Methods and Automatic Recurring Billing)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>Users may choose between bank transfer (unregistered/manual deposit) and automatic card billing as the payment method.</li>
          <li><strong>Bank transfer:</strong> Payment account details are provided by email or on the Service screen when applying for a plan upgrade, and the administrator activates the plan after confirming receipt of payment.</li>
          <li><strong>Automatic card billing (recurring payment):</strong>
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>Once a card is registered for payment, the fee is automatically charged to the registered card on each subsequent billing cycle (once a month, on the 1st of each month).</li>
              <li>The Company will notify users by email in advance of an upcoming automatic charge.</li>
              <li>To stop automatic billing, change the plan to the Free plan or delete the registered card; automatic billing will not occur from the next billing cycle following the change.</li>
              <li>Card payment information is processed through a payment gateway, and the Company does not directly store the original card number. See Article 5 (Outsourcing of Personal Information Processing) of the Privacy Policy for details.</li>
            </ul>
          </li>
          <li>If the plan is not applied within 1 business day after a bank transfer or card payment, please contact us by email (alt.generation@outlook.com).</li>
          <li>The Company issues tax invoices or cash receipts upon a user's request, and may separately collect the business information necessary to issue them (trade name, business registration number, etc.) before payment.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 9 (Team Service)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>A member may create a team, invite other members (team members), and share a plan and usage together.</li>
          <li>The member who creates a team (the "Team Owner") is responsible for billing of the team plan and has the authority and responsibility to invite/remove team members, delete the team, and transfer Team Owner rights.</li>
          <li>The Team Owner may view team members' service usage history (ALT text generation history, usage, etc.) for the purpose of managing the team.</li>
          <li>If a team member leaves the team or is removed by the Team Owner, that member may no longer use the benefits of the team plan and is converted to an individual plan. ALT text history generated while belonging to the team remains attributed to the team.</li>
          <li>Before leaving a team, the Team Owner must transfer Team Owner rights to another team member. The team's plan remains unchanged when Team Owner rights are transferred.</li>
          <li>If a team is deleted, use of the team plan ends immediately and team members can no longer use the Service. No refund is provided for the remaining period as a result of team deletion.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 10 (Restriction and Suspension of Service Use)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>The Company may restrict or suspend all or part of the Service where a national emergency such as war, conflict, or a natural disaster occurs or is likely to occur, or where a telecommunications carrier suspends telecommunications services under the Telecommunications Business Act, or for other unavoidable reasons.</li>
          <li>Where a user violates an obligation under these Terms or interferes with the normal operation of the Service, the Company may progressively restrict the user's use of the Service through warnings, temporary suspension, or permanent suspension.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 11 (Withdrawal of Subscription and Refund Policy)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>A member who has not used the Service after paying for a paid service (i.e., has not generated ALT text, made API calls, etc.) may request withdrawal of subscription (cancellation of payment) within 7 days of the payment date. If the requirements are met, the full payment amount will be refunded.</li>
          <li>Withdrawal of subscription and refunds are restricted in the following cases.
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>The Service has been used even once after payment (ALT text generation, API calls, etc.)</li>
              <li>More than 7 days have elapsed since the payment date</li>
              <li>Simple change of mind by the user, or failure to review the plan details before payment</li>
              <li>Paid services obtained free of charge through a promotion or event</li>
            </ul>
          </li>
          <li>Even where a refund is restricted, downgrading the plan allows continued use until the end of the current billing period, after which the plan automatically switches to the lower plan. No separate refund is provided for the remaining service in an already-paid period.</li>
          <li>In the event of a service outage attributable to the Company (lasting 24 hours or more), an extension of the usage period or a refund proportional to the outage period may be provided.</li>
          <li>Refund requests may be submitted by email (alt.generation@outlook.com), and the Company will process them within 3 business days of receipt.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 12 (Service Hours and Outages)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>The Service is, in principle, available 24 hours a day, year-round, but may be suspended with advance or after-the-fact notice in the following cases.
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>Scheduled system maintenance, upgrades, or urgent security patches</li>
              <li>Server/network failures, natural disasters, or national emergencies</li>
              <li>Outages at external AI service providers (e.g., OpenAI)</li>
            </ul>
          </li>
          <li>The Company is not liable for damages resulting from a service suspension unless caused by the Company's willful misconduct or gross negligence.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 13 (Prohibited Conduct)</h3>
        <p className="text-gray-700 leading-relaxed mb-2">Users must not engage in any of the following conduct.</p>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>Registering false information when signing up or using the Service</li>
          <li>Stealing or making unauthorized use of another person's account information</li>
          <li>Using the Service to infringe another person's copyright, right of publicity, or personal information</li>
          <li>Uploading or crawling images containing harmful content (obscene material, violence, hate speech, etc.)</li>
          <li>Disregarding a target site's robots.txt rules when crawling, or causing harm to a target site through excessive traffic</li>
          <li>Sharing an API key without authorization, or transferring or selling it to a third party</li>
          <li>Reverse-engineering the Service's source code, probing for vulnerabilities, unauthorized crawling, or other conduct that harms the system</li>
          <li>Using automated programs to exceed plan limits or otherwise abuse the Service</li>
          <li>Other conduct that violates applicable law, these Terms, or public order and morals</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 14 (Termination of the Agreement and Withdrawal of Membership)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>A member may apply for withdrawal at any time through the withdrawal menu or by email.</li>
          <li>If a member with an active paid subscription withdraws, the subscription is terminated immediately and no refund is provided for the remaining period. Automatic billing is also stopped if a card is registered.</li>
          <li>A member's data (ALT text history, API keys, etc.) is retained for 1 year after withdrawal, during which the member may cancel the withdrawal and restore the account. After 1 year, all data is automatically deleted and cannot be restored, except that information required to be retained under applicable law will be separately retained for the applicable period.</li>
          <li>Within 1 year of withdrawal, a member may cancel the withdrawal and restore the account by email. However, card payment information destroyed during the withdrawal process, terminated team memberships, and reset plans will not be restored even after the account is restored, and must be reconfigured if needed.</li>
          <li>A withdrawn member may re-register with the same email address after 30 days have elapsed from the date of withdrawal.</li>
          <li>The Company may terminate the service agreement or restrict use of the Service in any of the following cases.
            <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
              <li>The user has engaged in prohibited conduct under these Terms</li>
              <li>Fees have been in arrears for 30 days or more</li>
              <li>The Service has been used for an unlawful purpose</li>
            </ul>
          </li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 15 (Disclaimer)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>The Company is not liable for a service suspension caused by force majeure, such as a natural disaster, war, terrorism, or hacking.</li>
          <li><strong>AI-generated output:</strong> Because this Service uses AI models, the Company does not guarantee the accuracy, completeness, or suitability of generated ALT text. Final review and correction are the user's responsibility.</li>
          <li>Legal liability for copyright infringement, violation of terms of service, or similar issues relating to an external website crawled by a user rests with the user.</li>
          <li>Liability relating to copyright, right of publicity, personal information, or similar matters concerning images a user uploads to the Service rests with the user.</li>
          <li>The Company is not liable for a service disruption or degradation in quality caused by an outage or policy change at an external AI service provider (e.g., OpenAI).</li>
          <li>The Company is not liable for damages arising in connection with use of the free service unless caused by the Company's willful misconduct or gross negligence.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 16 (Dispute Resolution)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>Disputes relating to use of the Service will first be resolved by mutual agreement via email (alt.generation@outlook.com).</li>
          <li>If agreement cannot be reached, the dispute may be referred to mediation by a relevant body such as the Consumer Dispute Mediation Committee.</li>
        </ol>
      </section>

      <section>
        <h3 className="font-bold text-base mb-2">Article 17 (Governing Law and Jurisdiction)</h3>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
          <li>Matters relating to these Terms are governed by the laws of the Republic of Korea.</li>
          <li>Where litigation is filed regarding a dispute arising from use of the Service, it will be brought before the court having jurisdiction under the Civil Procedure Act and other relevant laws.</li>
        </ol>
      </section>

      <div className="pt-4 border-t mt-6 space-y-1">
        <p className="text-gray-600 text-xs">Announcement date: March 27, 2026</p>
        <p className="text-gray-600 text-xs">Effective date: April 1, 2026</p>
        <p className="text-gray-600 text-xs">Amended: August 10, 2026 (changed the jurisdiction clause to the jurisdiction determined under relevant law; adjusted the free plan's monthly limit to 40)</p>
        <p className="text-gray-600 text-xs">Amended: August 11, 2026 (corrected the automatic card billing cycle from "the same date each month based on the initial payment date" to "the 1st of each month," reflecting actual operation)</p>
        <p className="text-xs pt-1">
          <Link to="/terms/archive" target="_blank" rel="noopener noreferrer" className="text-primary underline">
            View previous versions
          </Link>
        </p>
      </div>
    </div>
  );
}
